<template>
  <div>
    <b-row class="inputFormRow">
      <b-col sm>
        <b-card no-body v-bind:class="{ invalid: commentLengthInvalid }">
          <b-card-header for="textarea">
            {{ this.commentLabel }}
          </b-card-header>
          <b-form-textarea
            id="textarea"
            :placeholder="$t('document.label.enterText')"
            rows="3"
            max-rows="5"
            v-model="comment"
          ></b-form-textarea>
          <div>
            <span v-if="commentLengthInvalid" class="invalid-text">
              {{ $t("document.label.toManyChars") }}
            </span>
            <span
              v-bind:class="[
                commentLengthInvalid ? 'limit-invalid' : '',
                'limit',
                'char-limit',
              ]"
            >
              {{ maxChars - comment.length }}
            </span>
          </div>
        </b-card>
      </b-col>
      <b-col sm>
        <b-card
          no-body
          :header="$t('document.label.file')"
          v-bind:class="{ invalid: totalFileSizeInvalid }"
        >
          <b-list-group v-for="file in uploadedFiles" :key="file[0]">
            <b-list-group-item>
              {{ file[0] }}
              <b-button
                class="remove-file"
                variant="outline-secondary"
                @click="removeFile(file[0])"
              >
                <i class="fas fa-trash"> </i>
              </b-button>
            </b-list-group-item>
          </b-list-group>
          <form enctype="multipart/form-data" novalidate>
            <div class="file-form-content">
              <!-- Note: Inputs are very hard to style. Here we display the
              actual input field transparently, it occupies the whole lower
              section of the card. It can be clicked and this opens the file
              selector. The button itself does nothing and is only there, so
              the user knows where to click. !-->
              <input
                ref="fileInput"
                type="file"
                multiple
                name="photos"
                @change="filesChange($event.target.files)"
                accept="image/*"
                class="transparent"
                :disabled="totalFileSizeInvalid"
              />
              <div>
                <b-button
                  variant="outline-secondary"
                  :disabled="totalFileSizeInvalid"
                >
                  {{
                    this.uploadedFiles.size === 0
                      ? $t("document.label.addFile")
                      : $t("document.label.addExtraFile")
                  }}
                </b-button>
              </div>
              <span
                v-bind:class="[
                  totalFileSizeInvalid ? 'limit-invalid' : '',
                  'limit',
                  'file-size-limit',
                ]"
              >
                {{ remainingFileSizeString }}
              </span>
            </div>
          </form>
        </b-card>
      </b-col>
    </b-row>
    <b-form-checkbox
      id="internalUseCheckbox"
      v-model="internalUse"
      value="true"
      unchecked-value="false"
    >
      {{ $t("document.label.internalUse") }}
    </b-form-checkbox>
    <hr />
  </div>
</template>

<script>
import store from "@/store/store.js";

export default {
  name: "Document",
  data() {
    return {
      commentString: "",
      maxChars: 500,
      maxTotalFileSize: 100000000, // bytes = 100MB
    };
  },
  computed: {
    uploadedFiles: {
      get() {
        return store.DocumentData.getUploadedFiles();
      },
      set(uploadedFiles) {
        store.DocumentData.setUploadedFiles(uploadedFiles);
      },
    },

    internalUse: {
      get() {
        return store.DocumentData.getInternalUse();
      },
      set(internalUse) {
        store.DocumentData.setInternalUse(internalUse);
      },
    },
    comment: {
      get() {
        return store.DocumentData.getComment();
      },
      set(comment) {
        store.DocumentData.setComment(comment);
      },
    },
    totalFileSize() {
      return store.DocumentData.getTotalUploadedFileSize();
    },
    remainingFileSizeString() {
      const totalFileSizeInMb = (this.totalFileSize / 1000000).toFixed(0);
      const maxFileSizeInMb = this.maxTotalFileSize / 1000000;
      return `${totalFileSizeInMb}MB/${maxFileSizeInMb}MB`;
    },
    commentLengthInvalid() {
      const isValid = this.comment.length <= this.maxChars;
      store.setValidComment(isValid);
      return !isValid;
    },
    totalFileSizeInvalid() {
      return !store.isValidTotalFileSize();
    },
  },
  props: {
    commentLabel: { type: String, default: "" },
  },
  methods: {
    removeFile(index) {
      store.DocumentData.removeFromUploadedFiles(index);
      this.$refs["fileInput"].value = "";
      const isValid = this.totalFileSize <= this.maxTotalFileSize;
      store.setValidTotalFileSize(isValid);
      this.$forceUpdate();
    },
    filesChange(fileList) {
      if (!fileList.length) return;
      store.DocumentData.addToUploadedFiles(fileList);
      const isValid = this.totalFileSize <= this.maxTotalFileSize;
      store.setValidTotalFileSize(isValid);
      this.$forceUpdate();
    },
  },
  mounted() {
    this.uploadedFiles = new Map();
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
span.limit {
  font-size: small;
  font-weight: 200;
}
span.limit-invalid {
  color: red;
  font-weight: normal;
}
span.char-limit {
  float: right;
}
span.invalid-text {
  color: red;
  font-size: small;
}
div.invalid {
  border-color: red;
}
div.invalid div {
  border-color: rgba(0, 0, 0, 0.125);
}
div.file-form-content {
  display: flex;
  justify-content: center;
}
button.remove-file {
  float: right;
}
input[type="file"].transparent {
  opacity: 0; /* invisible but it's there! */
  height: 40px;
  width: 100%;
  position: absolute;
  cursor: pointer;
}
span.file-size-limit {
  position: absolute;
  width: 100%;
  text-align: right;
  height: 0;
  overflow: visible;
}
</style>
